import { Headline } from '@almbrand/headline';
import { useIsInsideModal } from '@almbrand/modal';
import { useState } from 'react';
import { BasketAddMoreCard } from './AddMoreCard/BasketAddMoreCard';
import styles from './Basket.module.scss';
import { BasketProps } from './Basket.types';
import { BasketTile } from './BasketTile';
import { BasketBottomActions } from './BottomActions/BasketBottomActions';
import { DeleteProductConfirmationModal } from './DeleteProductConfirmationModal/DeleteProductConfirmationModal';
import { ExistingCustomerCard } from './ExistingCustomerCard/ExistingCustomerCard';
import { ProductDetailsModal } from './ProductDetailsModal/ProductDetailsModal';
import { ProductItem } from './ProductItem';
import { BasketSummaryCard } from './SummaryCard/BasketSummaryCard';

export const CURRENCY_SHORT = 'kr.';
export const CURRENCY_MONTHLY_SHORT = 'kr./md.';

export type { BasketItem, BasketItemDetails, BasketSummary, BasketSummaryLine } from './Basket.types';
export type { BasketProps };

export const Basket: React.FC<BasketProps> = (props) => {
	const {
		items,
		summary,
		themeName = 'Codan-theme',
		onBack,
		onCheckout,
		onEdit,
		onDelete,
		onDetails,
		title,
		backBtnLabel,
		checkoutBtnLabel,
		summaryCardTotal,
		summaryCardLegalText,
		deleteConfirmationTitle,
		deleteConfirmationDescription,
		deleteConfirmationConfirmBtnLabel,
	} = props;

	const [detailsItemId, setDetailsItemId] = useState<string | null>(null);
	const [pendingDeleteItemId, setPendingDeleteItemId] = useState<string | null>(null);
	const detailsItem = detailsItemId ? items.find(({ id }) => id === detailsItemId) : null;
	const hasPendingCalculation = items.some(({ price }) => price === undefined);
	const isInsideModal = useIsInsideModal();

	return (
		<>
			<section className={styles.Basket}>
				{title && (
					<Headline
						title={title}
						size='default'
						variant='h3'
						fontColor='primary'
					/>
				)}

				<ul className={styles.Basket__items}>
					{items.map(
						({
							id,
							price,
							previousPrice,
							title,
							subtitle,
							pendingCalculationText,
							detailsBtnLabel,
							deleteBtnLabel,
							editBtnLabel,
						}) => (
							<BasketTile
								key={id}
								element='li'
							>
								<ProductItem
									price={price}
									previousPrice={previousPrice}
									themeName={themeName}
									text={{
										title,
										subtitle,
										pendingCalculationText,
										detailsBtnLabel,
										deleteBtnLabel,
										editBtnLabel,
									}}
									onDetails={() => {
										setDetailsItemId(id);
										onDetails?.(id);
									}}
									onDelete={() => (isInsideModal ? onDelete?.(id) : setPendingDeleteItemId(id))}
									onEdit={() => onEdit?.(id)}
								/>
							</BasketTile>
						)
					)}
					{props.onExistingCustomer && (
						<ExistingCustomerCard
							element='li'
							themeName={themeName}
							onClick={props.onExistingCustomer}
							text={{
								title: props.existingCustomerTitle,
								subtitle: props.existingCustomerSubtitle,
							}}
						/>
					)}
					{props.onAddMore && (
						<BasketAddMoreCard
							element='li'
							themeName={themeName}
							onClick={props.onAddMore}
							ariaLabel={props.addMoreAriaLabel}
							text={{
								title: props.addMoreTitle,
								subtitle: props.addMoreSubtitle,
								ariaLabel: props.addMoreAriaLabel,
							}}
						/>
					)}
				</ul>

				{!hasPendingCalculation && (
					<BasketSummaryCard
						discountLines={summary.discountLines}
						totalAmount={summary.totalAmount}
						taxAmount={summary.taxAmount}
						text={{
							totalLabel: summaryCardTotal,
							legalText: summaryCardLegalText,
						}}
					/>
				)}

				<BasketBottomActions
					themeName={themeName}
					onBack={onBack}
					onCheckout={onCheckout}
					text={{ backBtnLabel, checkoutBtnLabel }}
				/>
			</section>

			{detailsItem?.details && (
				<ProductDetailsModal
					details={detailsItem.details}
					price={detailsItem.price}
					onClose={() => setDetailsItemId(null)}
				/>
			)}

			{pendingDeleteItemId && (
				<DeleteProductConfirmationModal
					themeName={themeName}
					onConfirm={() => {
						onDelete?.(pendingDeleteItemId);
						setPendingDeleteItemId(null);
					}}
					onCancel={() => setPendingDeleteItemId(null)}
					text={{
						title: deleteConfirmationTitle,
						description: deleteConfirmationDescription,
						confirmBtnLabel: deleteConfirmationConfirmBtnLabel,
					}}
				/>
			)}
		</>
	);
};
